// The portal's five areas (Concria portal plan §3, Phase 0 nav cut, 2026-10-10):
// Home, Deals, Shipments, Finance, Knowledge & Website. Plain data with icon NAMES so the
// node tests can import it; src/lib/nav.js swaps the names for lucide icons.
//
// Screens that are off the nav keep their routes, and their APIs keep their server-side
// permission gates. Hiding is not access control; the API is.

export const AREAS = Object.freeze(['Home', 'Deals', 'Shipments', 'Finance', 'Knowledge & Website']);

export const NAV_AREAS = [
  { label: 'Home', items: [{ to: '/', label: 'Home', icon: 'Home', hint: 'What needs attention today' }] },
  {
    label: 'Deals',
    items: [
      { to: '/leads', label: 'Leads', icon: 'Users', hint: 'Pipeline and website leads' },
      { to: '/bids', label: 'Bids & Specs', icon: 'Gavel', hint: 'Bids, specs, library' },
      { to: '/quotes', label: 'Quotes', icon: 'FileSignature', hint: 'Quotes and proposals' },
      { to: '/orders', label: 'Orders', icon: 'ClipboardList', hint: 'Sales orders' },
      { to: '/tasks', label: 'Tasks', icon: 'CheckSquare', hint: 'To-dos' },
      { to: '/accounts', label: 'Accounts', icon: 'Building2', hint: 'Companies' },
      { to: '/contacts', label: 'Contacts', icon: 'Contact', hint: 'People' },
      { to: '/projects', label: 'Watchlist', icon: 'Crosshair', hint: 'Projects we are tracking' },
    ],
  },
  { label: 'Shipments', items: [{ to: '/shipments', label: 'Shipments', icon: 'Ship', hint: 'Finland to site' }] },
  {
    label: 'Finance',
    items: [
      { to: '/invoices', label: 'Invoices', icon: 'BadgeDollarSign', hint: 'Billing and collections' },
      { to: '/books', label: 'Books', icon: 'BookOpen', hint: 'Ledger and deal payouts' },
      // Shown only to an agreement's partners and the owner (access from /api/partner-comp/me).
      { to: '/partner-comp', label: 'Partner pay', icon: 'Handshake', hint: 'The partners\' pay agreement', requires: 'partner_comp' },
      { to: '/concria-oy', label: 'Concria Oy', icon: 'Globe2', hint: 'Intercompany with Concria' },
      { to: '/concria-meet', label: 'Concria Meet', icon: 'CalendarCheck', hint: 'Weekly pack and KPIs' },
      { to: '/investments', label: 'Investments', icon: 'Wallet', hint: 'What each partner put in' },
      { to: '/catalog', label: 'Catalog', icon: 'Package', hint: 'Products and pricing' },
    ],
  },
  {
    label: 'Knowledge & Website',
    items: [{ to: '/marketing', label: 'Website & content', icon: 'Megaphone', hint: 'Website, chatbot documents, content drafts' }],
  },
];

// Off the nav in Phase 0 (code deletion waits for Phase 3). The routes still exist.
export const HIDDEN_FROM_NAV = Object.freeze(['/overview', '/proposals', '/kee', '/roadmap', '/soul', '/feedback-admin', '/billing']);

// Admin lives in the user menu, not in the nav.
export const USER_MENU = [{ to: '/admin', label: 'Admin Panel', icon: 'Shield' }];

// Website & content tabs. Target lists, Events and Datasheets are hidden in Phase 0.
export const MARKETING_TABS = Object.freeze([
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'content', label: 'Content' },
  { key: 'lists', label: 'Target lists', hidden: true },
  { key: 'events', label: 'Events', hidden: true },
  { key: 'website', label: 'Website' },
  { key: 'datasheets', label: 'Datasheets', hidden: true },
]);

export function visibleMarketingTabs() {
  return MARKETING_TABS.filter((t) => !t.hidden);
}

const isAdmin = (roles) => (roles || []).some((r) => String(r).toLowerCase() === 'admin');

/**
 * The nav for a user with these roles: the five areas, admin-only items dropped for others.
 * An item with `requires` shows only when access[requires] is true (the API decides; hidden
 * by default). Hiding is not access control; the API is.
 */
export function visibleAreas(roles = [], access = {}) {
  const admin = isAdmin(roles);
  const ok = (i) => (!i.adminOnly || admin) && (!i.requires || access[i.requires] === true);
  return NAV_AREAS.map((g) => ({ ...g, items: g.items.filter(ok) })).filter((g) => g.items.length);
}
